import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { formatAuPhoneForDisplay, normalizeAuPhone } from "../lib/phone.js";
import { formatDate } from "../lib/datetime.js";
import { ConcurrencyChart } from "./ConcurrencyChart.js";

interface BusinessRow {
  id: string;
  name: string;
}

interface NumberRow {
  e164: string;
  business_id: string;
  active: boolean;
  created_at: string;
  businesses: { name: string } | null;
}

export function Numbers() {
  const [rows, setRows] = useState<NumberRow[]>([]);
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [e164, setE164] = useState("");
  const [businessId, setBusinessId] = useState("");

  async function load() {
    setLoading(true);
    const [{ data: nums }, { data: biz }] = await Promise.all([
      supabase.from("phone_numbers").select("e164, business_id, active, created_at, businesses(name)").order("created_at", { ascending: false }),
      supabase.from("businesses").select("id, name").order("name"),
    ]);
    setRows((nums ?? []) as unknown as NumberRow[]);
    const list = (biz ?? []) as BusinessRow[];
    setBusinesses(list);
    if (!businessId && list[0]) setBusinessId(list[0].id);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!businessId || !e164) return;
    setError(null);
    // Typed the way people write it ("03 9235 2345"); stored as E.164, which is what call routing matches on.
    const normalized = normalizeAuPhone(e164, null);
    if (!normalized.ok) {
      setError(normalized.error);
      return;
    }
    const { error: err } = await supabase.from("phone_numbers").insert({ e164: normalized.e164, business_id: businessId });
    if (err) {
      setError(err.message);
      return;
    }
    setE164("");
    await load();
  }

  async function toggleActive(row: NumberRow) {
    await supabase.from("phone_numbers").update({ active: !row.active }).eq("e164", row.e164);
    await load();
  }

  async function removeNumber(row: NumberRow) {
    if (!window.confirm(`Delete ${formatAuPhoneForDisplay(row.e164)}? Calls to it will no longer be answered for ${row.businesses?.name ?? "this business"}.`)) return;
    setError(null);
    const { error: err } = await supabase.from("phone_numbers").delete().eq("e164", row.e164).eq("active", false);
    if (err) setError(err.message);
    await load();
  }

  async function reassign(row: NumberRow, newBusinessId: string) {
    await supabase.from("phone_numbers").update({ business_id: newBusinessId }).eq("e164", row.e164);
    await load();
  }

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div>
      <h2 className="rc-h2" style={{ marginBottom: 16 }}>Numbers</h2>

      <ConcurrencyChart />

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end" }}>
        <label style={{ fontSize: 13 }}>
          Number (E.164)
          <input required value={e164} onChange={(e) => setE164(e.target.value)} placeholder="03 9235 2345" style={fieldStyle} />
        </label>
        <label style={{ fontSize: 13 }}>
          Business
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} style={fieldStyle}>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" style={{ padding: "8px 16px" }}>
          Add
        </button>
      </form>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}

      <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
            <th style={{ padding: "10px 12px" }}>Number</th>
            <th style={{ padding: "10px 12px" }}>Business</th>
            <th style={{ padding: "10px 12px" }}>Active</th>
            <th style={{ padding: "10px 12px" }}>Added</th>
            <th style={{ padding: "10px 12px" }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.e164} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
              <td style={{ padding: "10px 12px" }}>{formatAuPhoneForDisplay(r.e164)}</td>
              <td style={{ padding: "10px 12px" }}>
                <select value={r.business_id} onChange={(e) => void reassign(r, e.target.value)} style={{ padding: 4 }}>
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: "10px 12px" }}>
                <button onClick={() => void toggleActive(r)} style={linkButtonStyle}>
                  {r.active ? "Yes (deactivate)" : "No (activate)"}
                </button>
              </td>
              <td style={{ padding: "10px 12px" }}>{formatDate(r.created_at)}</td>
              <td style={{ padding: "10px 12px" }}>
                {!r.active && (
                  <button onClick={() => void removeNumber(r)} style={{ ...linkButtonStyle, color: "var(--rc-danger)" }}>
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                No numbers yet.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>

    </div>
  );
}

const fieldStyle: React.CSSProperties = { display: "block", padding: 6, marginTop: 4 };
const linkButtonStyle: React.CSSProperties = { background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 13 };
